import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, btnStyle, labelStyle, tdStyle, thStyle, statCard } from '../theme';

const InventoryPage = ({ API_BASE }) => {
  const [items, setItems] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [valuation, setValuation] = useState({ itemCount:0, totalCostValue:0, totalSaleValue:0 });
  const [tab, setTab] = useState('list');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [moveForm, setMoveForm] = useState({ itemId:'', type:'in', quantity:'', reason:'' });
  const [showMoveForm, setShowMoveForm] = useState(false);

  function emptyForm() {
    return { name:'', category:'Consumable', unit:'pcs', currentStock:0, reorderLevel:5, costPrice:0, salePrice:0, hsnCode:'', supplier:'', batch:'', expiryDate:'' };
  }

  const fetchAll = async () => {
    try {
      const [i, l, e, v] = await Promise.all([
        axios.get(`${API_BASE}/inventory?active=true`),
        axios.get(`${API_BASE}/inventory/low-stock`),
        axios.get(`${API_BASE}/inventory/expiring?days=30`),
        axios.get(`${API_BASE}/inventory/valuation`),
      ]);
      setItems(i.data); setLowStock(l.data); setExpiring(e.data); setValuation(v.data);
    } catch (e) {}
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const handleSave = async () => {
    if (!form.name.trim()) return alert('Item name is required');
    try {
      if (editing) await axios.put(`${API_BASE}/inventory/${editing._id}`, form);
      else         await axios.post(`${API_BASE}/inventory`, form);
      setForm(emptyForm()); setEditing(null); setTab('list'); fetchAll();
    } catch (e) { alert('Save failed'); }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      name:item.name||'', category:item.category||'Consumable', unit:item.unit||'pcs',
      currentStock:item.currentStock||0, reorderLevel:item.reorderLevel||5,
      costPrice:item.costPrice||0, salePrice:item.salePrice||0,
      hsnCode:item.hsnCode||'', supplier:item.supplier||'', batch:item.batch||'',
      expiryDate: item.expiryDate ? new Date(item.expiryDate).toISOString().split('T')[0] : '',
    });
    setTab('add');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this item?')) return;
    await axios.delete(`${API_BASE}/inventory/${id}`);
    fetchAll();
  };

  const handleMovement = async () => {
    if (!moveForm.itemId || !moveForm.quantity) return alert('Select item and enter quantity');
    try {
      await axios.post(`${API_BASE}/inventory/movement`, {
        itemId: moveForm.itemId,
        type: moveForm.type,
        quantity: Number(moveForm.quantity),
        reason: moveForm.reason,
      });
      setMoveForm({ itemId:'', type:'in', quantity:'', reason:'' });
      setShowMoveForm(false);
      fetchAll();
    } catch (e) { alert('Movement failed'); }
  };

  const fmtINR = n => `₹${(n||0).toLocaleString('en-IN')}`;
  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';

  const tabBtn = (key, label) => (
    <button onClick={()=>setTab(key)}
      style={{padding:'9px 20px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.86rem',
        backgroundColor: tab===key ? theme.primary : '#e9ecef', color: tab===key ? 'white' : '#555'}}>
      {label}
    </button>
  );

  return (
    <div>
      {/* KPI */}
      <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'16px', marginBottom:'20px'}}>
        <div style={statCard}>
          <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Total Items</span>
          <h2 style={{margin:'4px 0 0', color:theme.navy, fontSize:'1.5rem', fontWeight:'700'}}>{valuation.itemCount}</h2>
        </div>
        <div style={statCard}>
          <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Cost Value</span>
          <h2 style={{margin:'4px 0 0', color:theme.info, fontSize:'1.5rem', fontWeight:'700'}}>{fmtINR(valuation.totalCostValue)}</h2>
        </div>
        <div style={{...statCard, borderLeft: lowStock.length > 0 ? `4px solid ${theme.danger}` : undefined}}>
          <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Low Stock Alerts</span>
          <h2 style={{margin:'4px 0 0', color: lowStock.length > 0 ? theme.danger : theme.success, fontSize:'1.5rem', fontWeight:'700'}}>{lowStock.length}</h2>
        </div>
        <div style={{...statCard, borderLeft: expiring.length > 0 ? `4px solid ${theme.warning}` : undefined}}>
          <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Expiring (30 days)</span>
          <h2 style={{margin:'4px 0 0', color: expiring.length > 0 ? theme.warning : theme.success, fontSize:'1.5rem', fontWeight:'700'}}>{expiring.length}</h2>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex', gap:'8px', marginBottom:'20px', flexWrap:'wrap'}}>
        {tabBtn('list', 'All Items')}
        {tabBtn('lowstock', `Low Stock (${lowStock.length})`)}
        {tabBtn('expiring', `Expiring (${expiring.length})`)}
        {tabBtn('add', editing ? 'Edit Item' : '+ Add Item')}
        <button onClick={()=>setShowMoveForm(!showMoveForm)}
          style={{padding:'9px 20px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.86rem',
            backgroundColor: showMoveForm ? theme.purple : '#e9ecef', color: showMoveForm ? 'white' : '#555'}}>
          📦 Stock Movement
        </button>
      </div>

      {/* Stock Movement Form */}
      {showMoveForm && (
        <div style={{...cardStyle, marginBottom:'20px', borderTop:`4px solid ${theme.purple}`}}>
          <h3 style={{margin:'0 0 14px', color:theme.navy}}>Record Stock Movement</h3>
          <div style={{display:'grid', gridTemplateColumns:'2fr 1fr 1fr 2fr', gap:'12px', marginBottom:'14px'}}>
            <div>
              <label style={labelStyle}>Item *</label>
              <select value={moveForm.itemId} onChange={e=>setMoveForm({...moveForm, itemId:e.target.value})} style={inputStyle}>
                <option value="">Select item...</option>
                {items.map(i => <option key={i._id} value={i._id}>{i.name} (Stock: {i.currentStock})</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Type</label>
              <select value={moveForm.type} onChange={e=>setMoveForm({...moveForm, type:e.target.value})} style={inputStyle}>
                <option value="in">Stock In</option>
                <option value="out">Stock Out</option>
                <option value="adjustment">Adjustment</option>
                <option value="wastage">Wastage</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Quantity *</label>
              <input type="number" value={moveForm.quantity} onChange={e=>setMoveForm({...moveForm, quantity:e.target.value})} style={inputStyle}/>
            </div>
            <div>
              <label style={labelStyle}>Reason</label>
              <input value={moveForm.reason} onChange={e=>setMoveForm({...moveForm, reason:e.target.value})} placeholder="Optional" style={inputStyle}/>
            </div>
          </div>
          <button onClick={handleMovement} style={{...btnStyle, marginTop:0, backgroundColor:theme.purple}}>Record Movement</button>
        </div>
      )}

      {/* Add/Edit Form */}
      {tab === 'add' && (
        <div style={{...cardStyle, marginBottom:'20px', borderTop:`4px solid ${theme.success}`}}>
          <h3 style={{margin:'0 0 16px', color:theme.navy}}>{editing ? 'Edit Item' : 'Add New Item'}</h3>
          <div style={{display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr', gap:'12px', marginBottom:'12px'}}>
            <div><label style={labelStyle}>Name *</label><input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} style={inputStyle}/></div>
            <div>
              <label style={labelStyle}>Category</label>
              <select value={form.category} onChange={e=>setForm({...form, category:e.target.value})} style={inputStyle}>
                <option>Consumable</option><option>Instrument</option><option>Material</option><option>Equipment</option><option>Other</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Unit</label>
              <select value={form.unit} onChange={e=>setForm({...form, unit:e.target.value})} style={inputStyle}>
                <option>pcs</option><option>ml</option><option>gm</option><option>pack</option><option>box</option><option>roll</option><option>tube</option>
              </select>
            </div>
            <div><label style={labelStyle}>Current Stock</label><input type="number" value={form.currentStock} onChange={e=>setForm({...form, currentStock:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Reorder Level</label><input type="number" value={form.reorderLevel} onChange={e=>setForm({...form, reorderLevel:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Cost Price ₹</label><input type="number" value={form.costPrice} onChange={e=>setForm({...form, costPrice:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Sale Price ₹</label><input type="number" value={form.salePrice} onChange={e=>setForm({...form, salePrice:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>HSN Code</label><input value={form.hsnCode} onChange={e=>setForm({...form, hsnCode:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Supplier</label><input value={form.supplier} onChange={e=>setForm({...form, supplier:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Batch</label><input value={form.batch} onChange={e=>setForm({...form, batch:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Expiry Date</label><input type="date" value={form.expiryDate} onChange={e=>setForm({...form, expiryDate:e.target.value})} style={inputStyle}/></div>
          </div>
          <div style={{display:'flex', gap:'10px'}}>
            <button onClick={handleSave} style={{...btnStyle, marginTop:0}}>{editing ? 'Update' : 'Add Item'}</button>
            {editing && <button onClick={()=>{setEditing(null);setForm(emptyForm());setTab('list');}} style={{...btnStyle, marginTop:0, backgroundColor:'#6c757d'}}>Cancel</button>}
          </div>
        </div>
      )}

      {/* Item Table */}
      {(tab === 'list' || tab === 'lowstock' || tab === 'expiring') && (
        <div style={cardStyle}>
          <table style={{width:'100%', borderCollapse:'collapse'}}>
            <thead>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Stock</th>
                <th style={thStyle}>Reorder</th>
                <th style={thStyle}>Cost</th>
                <th style={thStyle}>Supplier</th>
                <th style={thStyle}>Expiry</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {(tab === 'lowstock' ? lowStock : tab === 'expiring' ? expiring : items).map(item => {
                const isLow = item.currentStock <= item.reorderLevel;
                const isExp = item.expiryDate && new Date(item.expiryDate) < new Date(Date.now() + 30*86400000);
                return (
                  <tr key={item._id} style={{backgroundColor: isLow ? '#fff5f5' : isExp ? '#fffbeb' : 'transparent'}}>
                    <td style={tdStyle}><strong>{item.name}</strong></td>
                    <td style={tdStyle}>{item.category}</td>
                    <td style={{...tdStyle, fontWeight:'700', color: isLow ? theme.danger : theme.navy}}>
                      {item.currentStock} {item.unit}
                      {isLow && <span style={{marginLeft:'6px', fontSize:'0.68rem', padding:'2px 6px', borderRadius:'8px', backgroundColor:theme.danger+'18', color:theme.danger, fontWeight:'700'}}>LOW</span>}
                    </td>
                    <td style={tdStyle}>{item.reorderLevel}</td>
                    <td style={tdStyle}>{fmtINR(item.costPrice)}</td>
                    <td style={tdStyle}>{item.supplier || '—'}</td>
                    <td style={{...tdStyle, color: isExp ? theme.warning : theme.textMuted}}>
                      {fmtDate(item.expiryDate)}
                      {isExp && <span style={{marginLeft:'4px', fontSize:'0.68rem', color:theme.warning}}>⚠</span>}
                    </td>
                    <td style={tdStyle}>
                      <div style={{display:'flex', gap:'6px'}}>
                        <button onClick={()=>handleEdit(item)} style={{padding:'4px 10px', backgroundColor:'#0582ca', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>Edit</button>
                        <button onClick={()=>handleDelete(item._id)} style={{padding:'4px 10px', backgroundColor:theme.danger, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {(tab === 'lowstock' ? lowStock : tab === 'expiring' ? expiring : items).length === 0 && (
                <tr><td colSpan="8" style={{...tdStyle, textAlign:'center', color:'#aaa', padding:'40px'}}>No items found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InventoryPage;
